<template>
  <div class="grade-level-summary-row mb-6" v-if="shouldShowContainer">
    <v-row no-gutters>
      <v-col cols="12" class="pr-2">
        <status-card
          :color="containerColor"
          :is-container-layout="true"
          :header-above-card="false"
          :container-title="'Gateway Ratings Summary'"
          container-title-class="text-h6 font-weight-bold text-secondary"
          :container-footer-text="containerFooterText"
        >
          <v-row dense class="pt-2">
            <v-col
              v-for="gateway in gatewayData"
              :key="gateway.slug"
              cols="12"
              md="4"
              class="pb-0 mb-0"
            >
              <status-card
                :gateway-title="gateway.label"
                :report-slug="props.report.slug"
                :description="gateway.name"
                :score="gateway.score"
                :review-status-slug="gateway.review_status?.slug"
                :item-footer-text="gateway.review_status?.title || 'No Status'"
                :color="getGatewayColor(gateway.review_status?.slug)"
              />
            </v-col>
          </v-row>
        </status-card>
      </v-col>
    </v-row>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import type { Report } from '../../store';
  import { useGlossaryStore } from '../../store';
  import StatusCard from '../shared/StatusCard.vue';
  import { formatGradeName } from '../../lib/grades';

  const props = defineProps<{
    report: Report & { meets_expectations_footer_text?: string };
    selectedGrade?: string;
  }>();

  function getOrdinalSuffix(n: number): string {
    const j = n % 10,
      k = n % 100;
    if (j === 1 && k !== 11) return 'st';
    if (j === 2 && k !== 12) return 'nd';
    if (j === 3 && k !== 13) return 'rd';
    return 'th';
  }

  const gradeWordToNumberMap: Record<string, number> = {
    first: 1,
    second: 2,
    third: 3,
    fourth: 4,
    fifth: 5,
    sixth: 6,
    seventh: 7,
    eighth: 8,
    ninth: 9,
    tenth: 10,
    eleventh: 11,
    twelfth: 12,
  };

  const gradeDisplay = computed(() => {
    if (!props.selectedGrade) return '';

    const gradeSlug = props.selectedGrade.toLowerCase();

    if (gradeSlug === 'kindergarten' || gradeSlug === 'k') {
      return 'Kindergarten';
    }

    const gradeWord = gradeSlug.replace(/-grade$/, '');

    if (gradeWordToNumberMap[gradeWord]) {
      const gradeNumber = gradeWordToNumberMap[gradeWord];
      return `${gradeNumber}${getOrdinalSuffix(gradeNumber)}`;
    }

    if (/^\d+$/.test(gradeWord)) {
      const gradeNumber = parseInt(gradeWord, 10);
      return `${gradeNumber}${getOrdinalSuffix(gradeNumber)}`;
    }

    return props.selectedGrade;
  });

  const gatewayData = computed(() => {
    if (!props.report.gateways) return [];
    return props.report.gateways
      .map((gateway) => ({
        ...gateway,
        score: decodeHtmlEntities(gateway.score),
      }))
      .sort((a, b) => a.order - b.order);
  });

  function getGatewayColor(
    statusSlug: string | undefined,
  ): 'grey' | 'green' | 'orange' | 'red' | undefined {
    if (!statusSlug) return 'grey';

    const colorMap: Record<string, 'grey' | 'green' | 'orange' | 'red'> = {
      meets: 'green',
      'partially-meets': 'orange',
      'does-not-meet': 'red',
      'not-eligible': 'grey',
      'does-not-exist': 'grey',
    };

    return colorMap[statusSlug] || 'grey';
  }

  const glossaryStore = useGlossaryStore();

  function decodeHtmlEntities(text: string | null | undefined): string {
    if (!text) return '';
    if (typeof document !== 'undefined') {
      const textarea = document.createElement('textarea');
      textarea.innerHTML = text;
      return textarea.value;
    }
    return text
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&#37;/g, '%')
      .replace(/&#39;/g, "'");
  }

  const openGlossary = () => {
    glossaryStore.openGlossary('alignment summary');
  };

  const shouldShowContainer = computed(() => {
    return gatewayData.value.length > 0;
  });

  const containerColor = computed(() => {
    const footerText =
      props.report.meets_expectations_footer_text || 'Meets Expectations';
    if (footerText === 'Meets Expectations') {
      return undefined;
    }
    return 'red';
  });

  const containerFooterText = computed(() => {
    const footerText =
      props.report.meets_expectations_footer_text || 'Meets Expectations';
    if (footerText === 'Meets Expectations') {
      return undefined;
    }
    return footerText;
  });
</script>

<style lang="scss" scoped>
  .grade-level-summary-row {
    .vcard-header {
      border-bottom: 1px solid
        rgba(var(--v-border-color), var(--v-border-opacity));
      &.no-border {
        border-bottom: none;
      }
    }
  }
</style>
